import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserInvitations } from '@/hooks/useInvitations';
import { useEvent } from '@/hooks/useEvent';
import { useBudgetExpenses } from '@/hooks/useBudget';
import { useAllGuests } from '@/hooks/useGuests';
import { confirmedAttendees, pendingAttendees, expectedAttendees } from '@/lib/guests/headcount';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import CountdownWidget from '@/components/dashboard/CountdownWidget';
import BudgetWidget from '@/components/dashboard/BudgetWidget';
import GuestsWidget from '@/components/dashboard/GuestsWidget';
import TasksWidget from '@/components/dashboard/TasksWidget';
import InvitationsWidget from '@/components/dashboard/InvitationsWidget';
import PublishPaymentBanner from '@/components/dashboard/PublishPaymentBanner';
import { parseCalendarDate } from '@/lib/parseCalendarDate';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const { data: invitations, isLoading: loadingInvitations } = useUserInvitations();
  const { data: event } = useEvent();
  const { data: expenses = [] } = useBudgetExpenses();
  const { data: guests = [] } = useAllGuests();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Calculate budget stats from real data (006: fuente única = Evento)
  const budgetStats = useMemo(() => {
    const total = event?.total_budget || 0;
    const spent = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
    return { total, spent };
  }, [event, expenses]);

  // Calculate guest stats from real data
  const guestStats = useMemo(() => {
    const total = guests.length;
    const confirmed = guests.filter(g => g.rsvp_status === 'confirmed').length;
    const pending = guests.filter(g => g.rsvp_status === 'pending').length;
    // Asistentes (personas), no invitaciones. Los rechazados no se cuentan.
    return {
      total,
      confirmed,
      pending,
      confirmedAttendees: confirmedAttendees(guests),
      pendingAttendees: pendingAttendees(guests),
      expectedAttendees: expectedAttendees(guests),
    };
  }, [guests]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  // 006: nombres/fecha salen del Evento (fuente única). La invitación activa
  // (no archivada) se usa solo para el banner de publicación/pago.
  const primaryInvitation = invitations?.find((i) => !i.archived) ?? invitations?.[0];
  // TZ-safe: wedding_date es date-only guardado a medianoche UTC; con new Date(iso)
  // el header lo corría un día al oeste de UTC (5-sep se veía "4 de septiembre").
  const weddingDate =
    parseCalendarDate(event?.wedding_date) ??
    parseCalendarDate(primaryInvitation?.wedding_date) ??
    new Date(Date.now() + 90 * 24 * 60 * 60 * 1000); // Default: 90 days from now

  const brideName = event?.bride_name || primaryInvitation?.bride_name || '';
  const groomName = event?.groom_name || primaryInvitation?.groom_name || '';

  return (
    <div className="min-h-screen flex bg-background">
      <DashboardSidebar />
      
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        <DashboardHeader 
          brideName={brideName}
          groomName={groomName}
          weddingDate={weddingDate}
          onSignOut={handleSignOut}
        />
        
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
            {/* Publish/payment banner - reactive to payment status */}
            {primaryInvitation && (
              <PublishPaymentBanner invitation={primaryInvitation} />
            )}

            {/* Countdown - Full Width */}
            <CountdownWidget weddingDate={weddingDate} />
            
            {/* Stats Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Budget Widget */}
              <div className="lg:col-span-1">
                <BudgetWidget 
                  totalBudget={budgetStats.total} 
                  spent={budgetStats.spent} 
                />
              </div>
              
              {/* Tasks Widget */}
              <div className="lg:col-span-1">
                <TasksWidget />
              </div>
              
              {/* Invitations Widget */}
              <div className="lg:col-span-1">
                {!loadingInvitations && (
                  <InvitationsWidget invitations={invitations || []} />
                )}
              </div>
            </div>
            
            {/* Guests Stats - Full Width */}
            <div>
              <h3 className="font-serif text-lg font-medium text-foreground mb-4">
                Estado de Invitados
              </h3>
              <GuestsWidget
                total={guestStats.total}
                confirmed={guestStats.confirmed}
                pending={guestStats.pending}
                confirmedAttendees={guestStats.confirmedAttendees}
                pendingAttendees={guestStats.pendingAttendees}
                expectedAttendees={guestStats.expectedAttendees}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
